import { useState, useRef, useEffect } from "react";
import { categories, ctaButtons } from "@/utils/constants";

const articleLinks = [
  { label: "Strona główna", href: "/" },
  { label: "Poradnik zakupowy", href: "/artykul/kakobuy-jak-korzystac" },
  { label: "Bezpieczeństwo", href: "/artykul/czy-kakobuy-jest-bezpieczny" },
  { label: "Dostawa do Polski", href: "/artykul/kakobuy-shipping-polska" },
  { label: "Opinie", href: "/artykul/kakobuy-opinie" },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMobileSection = (section: string) => {
    setMobileOpenSection(mobileOpenSection === section ? null : section);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
              Kakobuy<span className="text-accent-500">Polska</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-5">
            {articleLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  link.label === "Strona główna"
                    ? "text-foreground-900 font-semibold hover:text-accent-500"
                    : "text-foreground-700 hover:text-foreground-900"
                }`}
              >
                {link.label}
              </a>
            ))}

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer bg-background-100 hover:bg-background-200 px-4 py-2 rounded-full"
              >
                Kategorie
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200/70 rounded-xl shadow-sm py-2 min-w-[240px] max-h-[450px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {ctaButtons.map((btn) => (
              <a
                key={btn.label}
                href={btn.url}
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  btn.label === "Buy It Now"
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                {btn.label}
              </a>
            ))}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-3">
            <a
              href="/"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-900 cursor-pointer border-b border-background-200/70"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-home-4-line text-primary-500 text-sm"></i>
              </span>
              Strona główna
            </a>
          </div>

          <div className="px-4 py-1">
            <button
              onClick={() => toggleMobileSection("articles")}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-article-line text-accent-500 text-sm"></i>
                </span>
                Poradniki i artykuły
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileOpenSection === "articles" ? "rotate-180" : ""}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>
            {mobileOpenSection === "articles" && (
              <div className="pl-9 pb-2 space-y-1">
                {articleLinks.filter(l => l.label !== "Strona główna").map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="block py-2.5 text-sm text-foreground-700 hover:text-foreground-900 cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-1">
            <button
              onClick={() => toggleMobileSection("categories")}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-grid-line text-secondary-500 text-sm"></i>
                </span>
                Kategorie produktów
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileOpenSection === "categories" ? "rotate-180" : ""}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>
            {mobileOpenSection === "categories" && (
              <div className="pl-9 pb-2 grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 cursor-pointer"
                  >
                    <span className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </span>
                    <span className="text-xs whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-4 border-t border-background-200/70 flex flex-col gap-2">
            {ctaButtons.map((btn) => (
              <a
                key={btn.label}
                href={btn.url}
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-semibold text-center py-3 rounded-full transition-all cursor-pointer ${
                  btn.label === "Buy It Now"
                    ? "bg-accent-500 text-background-50"
                    : "bg-primary-500 text-background-50"
                }`}
              >
                {btn.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}